import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Minus, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { BookingSheetFormProps } from "./types";

function Stepper({ value, onChange, min = 1, max = 99, label }: { value: number; onChange: (v: number) => void; min?: number; max?: number; label?: string }) {
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-sm text-muted-foreground mr-1">{label}</span>}
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
        <Minus className="h-3 w-3" />
      </Button>
      <span className="w-8 text-center text-sm font-medium">{value}</span>
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
}

interface BookingSheetCourtFormProps extends BookingSheetFormProps {
  customers: any[];
  selectedCustomerId: string | null;
  onSelectCustomer: (id: string | null, name?: string) => void;
  customerSearch: string;
  onCustomerSearch: (v: string) => void;
  autoPrice: number | null;
  autoPricePerCourt: number | null;
  totalCourtCount: number;
  activePrice: number;
  discountedPrice: number;
  paymentMethods: { value: string; label: string }[];
  organizationId: string | null;
  freeBookingReasons?: string[];
  allowFreeBookings?: boolean;
  customerPunchCards?: Array<{ id: string; name: string; remaining: number; expires_at: string | null }>;
  customerPunchCardsLoading?: boolean;
}

export default function BookingSheetCourtForm({
  formState,
  onFieldChange,
  customers,
  selectedCustomerId,
  onSelectCustomer,
  customerSearch,
  onCustomerSearch,
  autoPrice,
  autoPricePerCourt,
  totalCourtCount,
  activePrice,
  discountedPrice,
  organizationId,
  freeBookingReasons = [],
  allowFreeBookings = true,
  customerPunchCards = [],
  customerPunchCardsLoading = false,
}: BookingSheetCourtFormProps) {
  const { t } = useTranslation("admin");
  // PR-AY2b (mechanical apply of PR-AY2a Tables<> pattern): formState is
  // typed `Record<string, unknown>` in shared BookingSheetFormProps.
  // Per-site cast at destructure narrows for usage below.
  const {
    isCustomPrice,
    customPrice,
    customPriceNote,
    discount,
    discountType,
    paymentMethod,
    notifyCustomer,
    cancellationHours,
    ageGroup,
    voucherCode,
    appliedVoucher,
  } = formState as {
    isCustomPrice?: boolean;
    customPrice?: number | string;
    customPriceNote?: string;
    discount?: number | string;
    discountType?: string;
    paymentMethod?: string;
    notifyCustomer?: boolean;
    cancellationHours?: number;
    ageGroup?: string;
    voucherCode: string;
    appliedVoucher?: { code: string; discount_value: number; discount_type: string } | null;
  };

  const effectiveFreeReasons = freeBookingReasons.length > 0
    ? freeBookingReasons
    : [t("bookingSheet.freeReasonStaff"), t("bookingSheet.maintenance"), t("bookingSheet.event"), t("bookingSheet.other")];

  const selectedCustomer = customers.find((c: any) => c.id === selectedCustomerId);

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <div>
        <Label className="text-xs">{t("bookingSheet.customer")}</Label>
        <Input
          placeholder={t("bookingSheet.searchCustomer")}
          value={customerSearch}
          onChange={(e) => { onCustomerSearch(e.target.value); onSelectCustomer(null); }}
          className="h-9 text-sm"
        />
        {customers.length > 0 && !selectedCustomerId && customerSearch && (
          <div className="border rounded-md max-h-40 overflow-y-auto mt-1 shadow-sm">
            {customers.map((c: any) => (
              <button
                key={c.id}
                className="w-full text-left px-3 py-2 text-xs hover:bg-accent flex flex-col gap-0.5 border-b last:border-0"
                onClick={() => onSelectCustomer(c.id, c.full_name || c.email)}
              >
                <span className="font-medium">{c.full_name || t("bookingSheet.unnamed")}</span>
                <span className="text-muted-foreground">
                  {c.email}{c.phone ? ` · ${c.phone}` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
        {selectedCustomer && (
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-xs">{selectedCustomer.full_name || selectedCustomer.email}</Badge>
            <button onClick={() => { onSelectCustomer(null); onCustomerSearch(""); }} className="text-xs text-muted-foreground hover:text-foreground">×</button>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground mt-0.5">{t("bookingSheet.leaveEmptyWalkIn")}</p>
      </div>

      {/* Price */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs">
            {t("bookingSheet.price")} {isCustomPrice ? <span className="text-muted-foreground">({t("bookingSheet.manual")})</span> : <span className="text-muted-foreground">({t("bookingSheet.auto")})</span>}
          </Label>
          {isCustomPrice && (
            <button
              onClick={() => { onFieldChange("isCustomPrice", false); onFieldChange("customPrice", ""); }}
              className="text-[10px] text-primary hover:underline"
            >
              {t("bookingSheet.reset")}
            </button>
          )}
        </div>
        {isCustomPrice ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Input
                value={customPrice}
                onChange={(e) => onFieldChange("customPrice", e.target.value)}
                className="h-9 text-sm flex-1"                autoFocus
              />
              <span className="text-sm text-muted-foreground">kr</span>
            </div>
            <Input
              placeholder={t("bookingSheet.notePlaceholder")}
              value={customPriceNote}
              onChange={(e) => onFieldChange("customPriceNote", e.target.value)}
              className="h-7 text-sm"
            />
            {(() => {
              const manualStr = String(customPrice ?? "");
              if (!manualStr) return null;
              const manual = parseFloat(manualStr);
              if (isNaN(manual)) return null;
              if (autoPrice == null || isNaN(autoPrice)) return <p className="text-[10px] text-muted-foreground">{t("bookingSheet.manualPriceHint")}</p>;
              const auto = Number(autoPrice);
              if (manual === auto) return null;
              if (manual > auto) return <p className="text-[10px] text-muted-foreground">{t("bookingSheet.priceIncreasedFrom", { amount: auto })}</p>;
              return <p className="text-[10px] text-muted-foreground">{t("bookingSheet.discountedFrom", { amount: auto })}</p>;
            })()}
          </div>
        ) : (
          <>
            <button
              onClick={() => { onFieldChange("isCustomPrice", true); onFieldChange("customPrice", autoPrice ? String(autoPrice) : ""); }}
              className="flex items-center gap-1.5 h-9 w-full rounded-md border border-border bg-background px-3 text-sm hover:bg-accent/50 transition-colors text-left"
            >
              <span className="font-medium">{autoPrice != null ? `${autoPrice} kr` : "—"}</span>
            </button>
            {totalCourtCount > 1 && autoPricePerCourt != null && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t("bookingSheet.courtPriceBreakdown", { count: totalCourtCount, perCourt: autoPricePerCourt, total: autoPrice })}
              </p>
            )}
          </>
        )}
      </div>

      {/* Discount */}
      <div>
        <Label className="text-xs mb-1 block">{t("bookingSheet.discount")}</Label>
        <div className="flex items-center gap-1.5">
          <Input
            placeholder="0"
            value={discount}
            onChange={(e) => onFieldChange("discount", e.target.value)}
            className="h-9 text-sm flex-1"          />
          <div className="flex rounded-md border border-border overflow-hidden shrink-0">
            <button
              onClick={() => onFieldChange("discountType", "sek")}
              className={cn(
                "px-2.5 py-1.5 text-xs font-medium transition-colors",
                discountType === "sek" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-accent"
              )}
            >
              − kr
            </button>
            <button
              onClick={() => onFieldChange("discountType", "percent")}
              className={cn(
                "px-2.5 py-1.5 text-xs font-medium transition-colors border-l border-border",
                discountType === "percent" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-accent"
              )}
            >
              − %
            </button>
          </div>
        </div>
        {discount && Number(discount) > 0 && activePrice > 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            {activePrice} kr → <span className="font-medium text-foreground">{discountedPrice} kr</span>
            {" "}
            <span className="text-destructive">
              ({discountType === "percent" ? `−${discount}%` : `−${discount} kr`})
            </span>
          </p>
        )}
      </div>

      {/* Voucher code */}
      <div>
        <Label className="text-xs">{t("bookingSheet.voucherCode")}</Label>
        <div className="flex gap-1">
          <Input
            placeholder={t("bookingSheet.enterCode")}
            value={voucherCode}
            onChange={(e) => onFieldChange("voucherCode", e.target.value.toUpperCase())}
            className="h-7 text-sm font-mono uppercase"
            disabled={!!appliedVoucher}
          />
          {appliedVoucher ? (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => {
              onFieldChange("appliedVoucher", null);
              onFieldChange("voucherCode", "");
              onFieldChange("discount", "");
              onFieldChange("discountType", "sek");
            }}>
              ×
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
              if (!voucherCode.trim() || !organizationId) return;
              const { data, error } = await supabase
                .from("vouchers" as any)
                .select("*")
                .eq("code", voucherCode.trim())
                .eq("organization_id", organizationId)
                .maybeSingle();
              if (error || !data) { toast.error(t("bookingSheet.codeNotFound")); return; }
              const v = data as any;
              if (!v.is_active) { toast.error(t("bookingSheet.codeInactive")); return; }
              if (v.max_uses && v.uses_count >= v.max_uses) { toast.error(t("bookingSheet.codeMaxUsesReached")); return; }
              if (v.valid_until && new Date(v.valid_until) < new Date()) { toast.error(t("bookingSheet.codeExpired")); return; }
              if (v.valid_from && new Date(v.valid_from) > new Date()) { toast.error(t("bookingSheet.codeNotActiveYet")); return; }
              onFieldChange("appliedVoucher", v);
              // Sprint D Phase 4-aware read: percent type uses discount_percent;
              // fixed type uses discount_value_minor (öre → kr). Legacy discount_value
              // is read-only fallback for old rows.
              if (v.discount_type === "percent") {
                const pct = v.discount_percent ?? v.discount_value ?? 0;
                onFieldChange("discountType", "percent");
                onFieldChange("discount", String(pct));
              } else if (v.discount_type === "fixed") {
                const kr = v.discount_value_minor != null
                  ? v.discount_value_minor / 100
                  : (v.discount_value ?? 0);
                onFieldChange("discountType", "sek");
                onFieldChange("discount", String(kr));
              } else {
                onFieldChange("discountType", "percent");
                onFieldChange("discount", "100");
              }
              const displayValue = v.discount_type === "percent"
                ? `-${v.discount_percent ?? v.discount_value ?? 0}%`
                : v.discount_type === "free"
                  ? t("bookingSheet.free")
                  : `-${v.discount_value_minor != null ? v.discount_value_minor / 100 : (v.discount_value ?? 0)} kr`;
              toast.success(t("bookingSheet.codeAppliedSuccess", { code: v.code, discount: displayValue }));
            }}>
              {t("bookingSheet.apply")}
            </Button>
          )}
        </div>
        {appliedVoucher && (
          <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
            ✓ {t("bookingSheet.codeApplied", { code: appliedVoucher.code })}
          </p>
        )}
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.payment")}</Label>
        <Select value={paymentMethod} onValueChange={(v) => {
          onFieldChange("paymentMethod", v);
          if (v === "free") {
            onFieldChange("isCustomPrice", true);
            onFieldChange("customPrice", "0");
          }
        }}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="customer_pays">{t("bookingSheet.customerPays")}</SelectItem>
            <SelectItem value="paid_cash_swish">{t("bookingSheet.paidCashSwish")}</SelectItem>
            <SelectItem value="invoice">{t("bookingSheet.invoice")}</SelectItem>
            <SelectItem value="credits" disabled={!selectedCustomerId || customerPunchCards.length === 0}>
              {t("bookingSheet.punchCard")}{!selectedCustomerId ? ` (${t("bookingSheet.selectCustomerFirst")})` : customerPunchCards.length === 0 ? ` (${t("bookingSheet.noActivePunchCards")})` : ""}
            </SelectItem>
            {allowFreeBookings && <SelectItem value="free">{t("bookingSheet.freeInternal")}</SelectItem>}
            <SelectItem value="unpaid">{t("bookingSheet.unpaid")}</SelectItem>
          </SelectContent>
        </Select>
        {paymentMethod === "customer_pays" && (
          <div className="flex items-center justify-between mt-2">
            <Label className="text-xs">{t("bookingSheet.sendPaymentLink")}</Label>
            <Switch
              checked={formState.sendPaymentLink as boolean ?? false}
              onCheckedChange={(v) => onFieldChange("sendPaymentLink", v)}
            />
          </div>
        )}
        {paymentMethod === "paid_cash_swish" && (
          <div className="mt-2">
            <Label className="text-xs">{t("bookingSheet.paymentReference")}</Label>
            <Input
              placeholder={t("bookingSheet.paymentReferencePlaceholder")}
              value={(formState.paymentReference as string) ?? ""}
              onChange={(e) => onFieldChange("paymentReference", e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        )}
      </div>

      {/* Free booking reason (required audit trail) */}
      {paymentMethod === "free" && (
        <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
          <Label className="text-xs font-medium">{t("bookingSheet.reasonRequired")}</Label>
          <Select
            value={(formState.freeReason as string) ?? ""}
            onValueChange={(v) => onFieldChange("freeReason", v)}
          >
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder={t("bookingSheet.selectReason")} /></SelectTrigger>
            <SelectContent>
              {effectiveFreeReasons.map((reason) => (
                <SelectItem key={reason} value={reason.toLowerCase().replace(/[^a-zåäö0-9]/g, "_")}>{reason}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {((formState.freeReason as string) === "other" || (formState.freeReason as string) === "annat" || (formState.freeReason as string) === "övrigt") && (
            <Input
              placeholder={t("bookingSheet.describeReason")}
              value={(formState.freeReasonText as string) ?? ""}
              onChange={(e) => onFieldChange("freeReasonText", e.target.value)}
              className="h-8 text-sm"
            />
          )}
        </div>
      )}

      {/* Klippkort display + selection */}
      {paymentMethod === "credits" && selectedCustomerId && (
        <div className="space-y-2 rounded-lg border p-3">
          <Label className="text-xs font-medium">{t("bookingSheet.selectPunchCard")}</Label>
          {customerPunchCardsLoading ? (
            <p className="text-xs text-muted-foreground">{t("bookingSheet.loading")}</p>
          ) : customerPunchCards.length === 0 ? (
            <p className="text-xs text-destructive">{t("bookingSheet.noActivePunchCardsForCustomer")}</p>
          ) : (
            <div className="space-y-1.5">
              {customerPunchCards.map((card) => {
                const isSelected = (formState.selectedPunchCardId as string) === card.id;
                return (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => onFieldChange("selectedPunchCardId", card.id)}
                    className={cn(
                      "flex items-center justify-between text-xs border rounded px-2 py-2 w-full text-left transition-colors",
                      isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "hover:bg-accent"
                    )}
                  >
                    <span className="font-medium">{card.name}</span>
                    <span className="text-muted-foreground">
                      {t("bookingSheet.clipsRemaining", { count: card.remaining })}
                      {card.expires_at && ` · ${t("bookingSheet.validUntil")} ${card.expires_at.slice(0, 10)}`}
                    </span>
                  </button>
                );
              })}
              {customerPunchCards.length === 1 && !(formState.selectedPunchCardId as string) && (
                <p className="text-[10px] text-muted-foreground">{t("bookingSheet.oneClipDeducted")}</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.notifyCustomer")}</Label>
        <Switch checked={notifyCustomer} onCheckedChange={(v) => onFieldChange("notifyCustomer", v)} />
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.cancellationTime")}</Label>
        <Stepper value={cancellationHours} onChange={(v) => onFieldChange("cancellationHours", v)} min={0} max={168} label={t("bookingSheet.hours")} />
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.ageGroup")}</Label>
        <Select value={ageGroup} onValueChange={(v) => onFieldChange("ageGroup", v)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("bookingSheet.all")}</SelectItem>
            <SelectItem value="adult">{t("bookingSheet.adults")}</SelectItem>
            <SelectItem value="junior">{t("bookingSheet.juniors")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
